////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * FORGET PASSWORD PEA - PASSWORD RESET PRESENTATION (POD/PEA PATTERN)
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURAL ROLE: Pure UI component for password reset flow
 *
 * I[R{DVT}]O PATTERN:
 *   INPUT: Email input from user, success/loading states from Pod
 *   REQUIREMENTS: Email validation UI, success confirmation display, back navigation
 *   OUTPUT: Password reset request form or success confirmation
 *
 * UX: Dual-state component (form view → success view) with clear instructions
 */

import React, { useState } from 'react';
import styles from './Bitsbound_Kings_Customer_Auth_SRC_ForgetPassword_Lobby_Pea.module.css';

interface ForgetPasswordPeaProps {
  email: string;
  isLoading: boolean;
  isSuccess: boolean;
  error: string | null;
  onEmailChange: (email: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBackToSignIn: () => void;
}

const Pea_Lobby_ForgetPassword_SRC_Auth_Customer_Kings: React.FC<ForgetPasswordPeaProps> = ({
  email,
  isLoading,
  isSuccess,
  error,
  onEmailChange,
  onSubmit,
  onBackToSignIn
}) => {
  const [isEmailValid, setIsEmailValid] = useState(true);

  const validateEmail = (emailValue: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    onEmailChange(newEmail);
    setIsEmailValid(validateEmail(newEmail));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateEmail(email) && !isLoading) {
      onSubmit(e);
    }
  };

  if (isSuccess) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successIcon}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
          </div>
          
          <h1 className={styles.title}>Check Your Email</h1>
          
          <p className={styles.description}>
            We've sent a password reset link to:
          </p>
          
          <p className={styles.emailDisplay}>{email}</p>
          
          <div className={styles.instructions}>
            <p>Please check your email and click the link to reset your password.</p>
            <p className={styles.note}>
              The link will expire in 1 hour for security reasons.
            </p>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              onClick={onBackToSignIn}
              className={styles.secondaryButton}
            >
              Back to Login
            </button>
            
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Forgot Your Password?</h1>
          <p className={styles.description}>
            No worries! Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              className={`${styles.input} ${!isEmailValid && email ? styles.inputError : ''}`}
              placeholder="Enter your email"
              required
              autoComplete="email"
              autoFocus
            />
            {!isEmailValid && email && (
              <span className={styles.errorText}>
                Please enter a valid email address
              </span>
            )}
          </div>

          {error && (
            <div className={styles.errorMessage}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading || !email || !isEmailValid}
          >
            {isLoading ? (
              <>
                <span className={styles.spinner}></span>
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <button
            type="button"
            onClick={onBackToSignIn}
            className={styles.linkButton}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to Login
          </button>
        </div>

        <div className={styles.helpSection}>
          <p className={styles.helpText}>
            Still having trouble? <a href="/support" className={styles.supportLink}>Contact Support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pea_Lobby_ForgetPassword_SRC_Auth_Customer_Kings;